/**
 * المرحلة 1.4 (P14) — the visit's vital signs.
 *
 * Typed in the room (Arabic or Latin digits, the Arabic decimal mark too),
 * kept as Observations on the patient's one readings table (rule 4), so they
 * join the curve the patient's own readings and the nurse's already draw. A
 * reading outside the adult normal range is flagged as it is saved, unless
 * the institution's `visitVitals` policy turned the alerts off; the readings
 * it requires before closing are marked.
 */

import { useMemo, useState } from 'react';
import { Button, Icon, Input } from '../../components/common';
import { encounterApi } from '../../api/endpoints';
import { alertsFor, blankVitals, KIND_RANGES, NORMAL_RANGES, VITAL_KINDS, vitalsPayload } from '../../domain/encounter';
import { formatDateTime } from '../../domain/locale';
import { useI18n } from '../../i18n/I18nProvider';
import { CHARTS, MeasurementCurve } from '../patient/Measurements';

const GROUP_ICON = { bp: 'heart-pulse', hr: 'activity', temp: 'thermometer', spo2: 'droplets', rr: 'wind', glucose: 'droplet', weight: 'scale', height: 'ruler' };
const fmt = (n) => (Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10));

export function VitalsPanel({ encounter, write, compact = false }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(blankVitals);
  const [bad, setBad] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const policy = useMemo(() => (encounter.policy && encounter.policy.visitVitals) || { required: [], alerts: 'standard' }, [encounter.policy]);
  const canWrite = Boolean(encounter.can && encounter.can.write);
  const rows = useMemo(() => encounter.vitals || [], [encounter.vitals]);
  const flagged = useMemo(() => new Map(alertsFor(rows, policy).map((a) => [`${a.kind}|${a.value}`, a.level])), [rows, policy]);
  const charts = CHARTS.filter((c) => (encounter.trend || []).some((p) => c.kinds.includes(p.kind)));
  const [chart, setChart] = useState(null);
  const shown = charts.find((c) => c.id === chart) || charts[0] || null;
  const what = t('visitPrint.what.vitals');

  const set = (kind, value) => {
    setForm((f) => ({ ...f, [kind]: value }));
    if (bad.includes(kind)) setBad((b) => b.filter((k) => k !== kind));
  };

  const save = async () => {
    const { readings, invalid } = vitalsPayload(form);
    setBad(invalid);
    if (invalid.length) return;
    if (!Object.keys(readings).length) return;
    const res = await write((reason) => encounterApi.addVitals(encounter.id, { readings, ...(reason ? { reason } : {}) }), { ok: t('vitals14.saved'), what });
    if (res) {
      setForm(blankVitals());
      setAlerts(res.alerts || []);
    }
  };
  const remove = (row) => write((reason) => encounterApi.removeVital(encounter.id, row.id, reason), { ok: t('vitals14.removed'), what: t('visitPrint.what.vital_removed') });

  const range = (kind) => {
    const r = KIND_RANGES[kind];
    const n = NORMAL_RANGES[kind];
    return n ? t('vitals14.normal', { low: n.low, high: n.high }) : t('vitals14.accepted', { min: r.min, max: r.max });
  };
  const typed = Object.values(form).some((v) => String(v).trim());

  return (
    <div data-testid="vitals-panel">
      {canWrite && (
        <>
          <div className="vit-grid">
            {Object.entries(VITAL_KINDS).map(([group, kinds]) => {
              const isBad = kinds.some((k) => bad.includes(k));
              const required = (policy.required || []).includes(group);
              return (
                <div key={group} className={`vit-cell${isBad ? ' is-bad' : ''}`}>
                  <label htmlFor={`vit-${encounter.id}-${kinds[0]}`}>
                    <Icon name={GROUP_ICON[group]} size={13} />
                    {t(`vitals14.groups.${group}`)}
                    {required && <span className="soap-required">*</span>}
                  </label>
                  {kinds.length === 2 ? (
                    <div className="vit-pair">
                      <Input
                        id={`vit-${encounter.id}-${kinds[0]}`}
                        inputMode="decimal"
                        value={form[kinds[0]]}
                        onChange={(e) => set(kinds[0], e.target.value)}
                        placeholder="120"
                        aria-label={t(`visitPrint.kinds.${kinds[0]}`)}
                        data-testid={`vit-${kinds[0]}`}
                      />
                      <span className="text-slate-500">/</span>
                      <Input
                        inputMode="decimal"
                        value={form[kinds[1]]}
                        onChange={(e) => set(kinds[1], e.target.value)}
                        placeholder="80"
                        aria-label={t(`visitPrint.kinds.${kinds[1]}`)}
                        data-testid={`vit-${kinds[1]}`}
                      />
                    </div>
                  ) : (
                    <Input
                      id={`vit-${encounter.id}-${kinds[0]}`}
                      inputMode="decimal"
                      value={form[kinds[0]]}
                      onChange={(e) => set(kinds[0], e.target.value)}
                      data-testid={`vit-${kinds[0]}`}
                    />
                  )}
                  <span className="vit-unit">
                    {KIND_RANGES[kinds[0]].unit}
                    {!compact && <> · {range(kinds[0])}</>}
                  </span>
                </div>
              );
            })}
          </div>
          {bad.length > 0 && (
            <p className="mt-2 text-xs text-rose-300">{t('vitals14.outOfRange', { kinds: bad.map((k) => t(`visitPrint.kinds.${k}`)).join('، ') })}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
            <Button icon="save" onClick={save} disabled={!typed} data-testid="vitals-save">
              {t('vitals14.save')}
            </Button>
          </div>
          {alerts.length > 0 && (
            <div className="vit-alerts" role="status" data-testid="vitals-alerts">
              {alerts.map((a) => (
                <span key={`${a.kind}-${a.value}`} className={`vit-flag is-${a.level}`}>
                  <Icon name={a.level === 'high' ? 'arrow-up' : 'arrow-down'} size={13} />
                  {t(`vitals14.flag.${a.level}`, { kind: t(`visitPrint.kinds.${a.kind}`), value: fmt(a.value), low: a.range.low, high: a.range.high })}
                </span>
              ))}
            </div>
          )}
        </>
      )}

      <div className={canWrite ? 'mt-5' : ''}>
        <h4 className="enc-card-title">
          <Icon name="list" size={16} />
          {t('vitals14.thisVisit')}
          <span className="enc-count">{rows.length}</span>
        </h4>
        {rows.length ? (
          <div className="vit-scroll">
            <table className="vit-table" data-testid="vitals-table">
              <thead>
                <tr>
                  <th>{t('visitPrint.reading')}</th>
                  <th>{t('visitPrint.value')}</th>
                  <th>{t('visitPrint.time')}</th>
                  {canWrite && <th aria-label={t('vitals14.remove')} />}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const level = flagged.get(`${r.kind}|${Number(r.value)}`);
                  return (
                    <tr key={r.id}>
                      <td>{t(`visitPrint.kinds.${r.kind}`)}</td>
                      <td className={level ? `is-${level}` : ''} dir="ltr">
                        {fmt(Number(r.value))} {r.unit}
                        {level && <Icon name={level === 'high' ? 'arrow-up' : 'arrow-down'} size={12} className="ms-1 inline" />}
                      </td>
                      <td>{formatDateTime(r.at, { lang })}</td>
                      {canWrite && (
                        <td className="text-end">
                          <button type="button" className="enc-icon-btn is-danger" aria-label={t('vitals14.remove')} title={t('vitals14.remove')} onClick={() => remove(r)}>
                            <Icon name="trash-2" size={15} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="enc-empty">{t('vitals14.none')}</p>
        )}
      </div>

      {!compact && shown && (
        <div className="mt-5" data-testid="vitals-trend">
          <h4 className="enc-card-title">
            <Icon name="activity" size={16} />
            {t('vitals14.trend')}
          </h4>
          <div className="vit-charts" role="group" aria-label={t('vitals14.trend')}>
            {charts.map((c) => (
              <button key={c.id} type="button" aria-pressed={c.id === shown.id} onClick={() => setChart(c.id)}>
                {t(`measurements.chart.${c.id}`)}
              </button>
            ))}
          </div>
          <MeasurementCurve points={encounter.trend || []} kinds={shown.kinds} unit={shown.unit} height={200} />
        </div>
      )}
    </div>
  );
}

export default VitalsPanel;
